import { useCMSPageField } from '@/cms/fields'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const Wrapper = styled.div`
  width: 100%;
  padding: 0 ${space('primitives_space_16')};
  margin: 0 auto;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    max-width: 100%;
    padding: 0 ${space('primitives_space_32')};
    width: ${tokPx('primitives_area_size_720')};
  }
  @media ${mqMin('primitives_breakpoint_l_min')} {
    width: ${tokPx('primitives_area_size_1440')};
  }
`

const HeroContentContainer = styled.div`
  background-color: #fff;
  max-width: 100%;
  padding: ${space('primitives_space_24')};
  border-radius: ${tokPx('semantics_boxes_corner_radius')};
  position: relative;
  margin-top: -${space('primitives_space_40')};
  box-shadow: ${tok('primitives_box_shadows_level_2')};
  display: flex;
  flex-direction: column;
  align-items: center;

  > * {
    margin-bottom: ${space('primitives_space_6')};

    &:last-child {
      margin-bottom: initial;
    }
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    min-width: ${tokPx('primitives_area_size_480')};
    margin-top: -${space('primitives_space_80')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    max-width: ${tokPx('primitives_area_size_720')};
    min-width: ${tokPx('primitives_area_size_640')};
    left: ${tokPx('primitives_area_size_160')};
    margin-top: -${tokPx('primitives_area_size_160')};
  }
`

const HeroTitle = styled(Typography)`
  ${Typography.modVariant('display-g-regular')}

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Typography.modVariant('display-f-regular')}
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('display-e-regular')}
  }
`

const HeroDescription = styled(Typography)`
  ${Typography.modVariant('body-m-regular-snug')}
`

const HeroContainer = styled.div`
  position: relative;
  width: 100%;
  user-select: none;
  display: flex;
  flex-direction: column;
  height: calc(100% - ${space('primitives_space_40')});
  margin-bottom: ${space('primitives_space_16')};
`

const BackgroundImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: ${tokPx('primitives_area_size_120')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: ${tokPx('primitives_area_size_240')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    height: ${tokPx('primitives_area_size_320')};
  }
`
const LogoImage = styled.img`
  width: 117px;
  height: 40px;
  align-self: flex-start;
  object-fit: cover;
`

export function Hero() {
  const intl = useIntl()
  const groupsHeader = useCMSPageField('Groups.header') ?? null
  const { data } = useBookingDraftQuery()

  const { backgroundImageUrl, logoUrl, title, description } = groupsHeader ?? {}
  if (!data || !groupsHeader) {
    return null
  }

  const heroTitle = (title && title.replace('{group_name}', data?.groupInformation?.name ?? '')) || intl.formatMessage(
    { id: 'groups_checkout_steps_extra_services_title' },
    { group_name: data?.groupInformation?.name ?? '' },
  )
  const heroDescription = description || intl.formatMessage({ id: 'groups_checkout_steps_extra_services_description' })

  return (
    <HeroContainer>
      {backgroundImageUrl
        ? (
            <BackgroundImage
              src={buildImgSrc(backgroundImageUrl, 1920, 399)}
              srcSet={buildImgSrcSet(backgroundImageUrl, [375, 600, 720, 1440, 1440, 2000, 2400])}
              sizes="100vw"
              alt=""
            />
          )
        : null}
      <Wrapper>
        <HeroContentContainer>
          {logoUrl && (
            <LogoImage
              src={buildImgSrc(logoUrl, 117, 40) ?? ''}
              srcSet={buildImgSrc(logoUrl, 117, 40) ?? ''}
              alt=""
            />
          )}
          <HeroTitle>
            {heroTitle}
          </HeroTitle>
          <HeroDescription>
            {heroDescription }
          </HeroDescription>
        </HeroContentContainer>
      </Wrapper>
    </HeroContainer>
  )
}
